const COOKIE_NAME = "spoti_ref";
const LS_KEY = "spoti_ref_code";

export function setReferralCode(code: string, durationDays = 90) {
  const maxAge = durationDays * 24 * 60 * 60;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(code)};max-age=${maxAge};path=/;SameSite=Lax`;
  try { localStorage.setItem(LS_KEY, code); } catch {}
}

export function getReferralCode(): string | null {
  // Try cookie first
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  if (match) return decodeURIComponent(match[1]);
  // Fallback to localStorage
  try { return localStorage.getItem(LS_KEY); } catch { return null; }
}

export function clearReferralCode() {
  document.cookie = `${COOKIE_NAME}=;max-age=0;path=/;SameSite=Lax`;
  try { localStorage.removeItem(LS_KEY); } catch {}
}

export function generateReferralCode(): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let suffix = "";
  for (let i = 0; i < 5; i++) suffix += chars[Math.floor(Math.random() * chars.length)];
  return `BEATS-${suffix}`;
}

export type ReferralTier = "none" | "bronze" | "silver" | "gold";

export interface ReferralSettings {
  cookie_duration_days: number;
  new_user_discount_pct: number;
  tier_bronze_min: number;
  tier_bronze_commission_pct: number;
  tier_silver_min: number;
  tier_silver_commission_pct: number;
  tier_gold_min: number;
  tier_gold_commission_pct: number;
}

export function getTier(referralCount: number, settings: ReferralSettings): ReferralTier {
  if (referralCount >= settings.tier_gold_min) return "gold";
  if (referralCount >= settings.tier_silver_min) return "silver";
  if (referralCount >= settings.tier_bronze_min) return "bronze";
  return "none";
}

export function getCommissionPct(tier: ReferralTier, settings: ReferralSettings): number {
  switch (tier) {
    case "gold": return Number(settings.tier_gold_commission_pct);
    case "silver": return Number(settings.tier_silver_commission_pct);
    case "bronze": return Number(settings.tier_bronze_commission_pct);
    default: return 0;
  }
}

export function getNextTier(tier: ReferralTier): ReferralTier | null {
  switch (tier) {
    case "none": return "bronze";
    case "bronze": return "silver";
    case "silver": return "gold";
    case "gold": return null;
  }
}

export function getNextTierMin(tier: ReferralTier, settings: ReferralSettings): number {
  const next = getNextTier(tier);
  if (!next) return 0;
  switch (next) {
    case "bronze": return settings.tier_bronze_min;
    case "silver": return settings.tier_silver_min;
    case "gold": return settings.tier_gold_min;
    default: return 0;
  }
}

export const TIER_COLORS: Record<ReferralTier, { bg: string; text: string; border: string; icon: string }> = {
  none: { bg: "bg-muted", text: "text-muted-foreground", border: "border-border", icon: "⭐" },
  bronze: { bg: "bg-amber-700/15", text: "text-amber-700", border: "border-amber-700/30", icon: "🥉" },
  silver: { bg: "bg-slate-400/15", text: "text-slate-500", border: "border-slate-400/30", icon: "🥈" },
  gold: { bg: "bg-yellow-500/15", text: "text-yellow-600", border: "border-yellow-500/30", icon: "🥇" },
};

export function maskEmail(email: string): string {
  const [user, domain] = email.split("@");
  if (!domain) return email;
  return user.slice(0, 2) + "***@" + domain;
}
